import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtINR, fmtINRCompact } from "@/lib/format";
import {
    TrendingUp, TrendingDown, Users, Building2, Sparkles, Clock,
    LineChart, IndianRupee, AlertTriangle, Activity, Globe, HeartHandshake,
    ChevronRight,
} from "lucide-react";

const PERIODS = [
    { id: "today",   label: "Today"      },
    { id: "7d",      label: "Last 7 Days"},
    { id: "30d",     label: "Last 30 Days"},
    { id: "quarter", label: "Quarter"    },
    { id: "year",    label: "Year"       },
];

function DeltaChip({ value, pct }) {
    if (pct === null || pct === undefined) {
        return <span className="text-[10px] text-stone-400 uppercase tracking-widest">no comparison</span>;
    }
    const up = pct >= 0;
    const Icon = up ? TrendingUp : TrendingDown;
    return (
        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold ${up ? "text-emerald-700" : "text-rose-700"}`}>
            <Icon size={11} /> {up ? "+" : ""}{pct}%
        </span>
    );
}

function Kpi({ icon: Icon, label, value, sub, delta, tone = "neutral", testId }) {
    const toneRing = {
        gold:    "ring-amber-200 bg-amber-50/50",
        emerald: "ring-emerald-100 bg-emerald-50/40",
        neutral: "ring-stone-200 bg-white",
    }[tone];
    return (
        <div className={`rounded-2xl ring-1 ${toneRing} p-5`} data-testid={testId}>
            <div className="flex items-center gap-2 mb-3">
                <Icon size={13} className="text-stone-500" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">{label}</span>
            </div>
            <div className="text-3xl font-semibold text-stone-900 tabular-nums leading-none">{value}</div>
            <div className="flex items-center justify-between gap-2 mt-3">
                {sub ? <span className="text-[11px] text-stone-500">{sub}</span> : <span />}
                {delta && <DeltaChip {...delta} />}
            </div>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="mt-8">
            <div className="flex items-end justify-between mb-3">
                <div>
                    <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500">{title}</h2>
                    {subtitle && <p className="text-sm text-stone-600 mt-1">{subtitle}</p>}
                </div>
            </div>
            {children}
        </div>
    );
}

export default function SuperAdminCommandCentre() {
    const [period, setPeriod] = useState("30d");
    const [d, setD] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/super-admin/command-centre?period=${period}`)
            .then((r) => setD(r.data))
            .catch((e) => setErr(e.response?.data?.detail || "Failed to load"));
    }, [period]);

    if (err) return <div className="p-6 text-rose-700">{err}</div>;
    if (!d) return <div className="p-6 text-stone-500">Loading command centre…</div>;

    const k = d.kpis;

    return (
        <div className="max-w-[1400px] mx-auto" data-testid="command-centre-page">
            {/* ═══ Executive header ═══ */}
            <div className="mb-6 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="eyebrow text-amber-700">VAULT Command Centre</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Business performance
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Customer growth, subscription revenue and platform health — at a glance. Metrics update live from SaaS operational data.
                    </p>
                </div>
                <div className="flex flex-wrap gap-1 rounded-full border border-stone-200 bg-white p-1" data-testid="cc-period-selector">
                    {PERIODS.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => setPeriod(p.id)}
                            data-testid={`cc-period-${p.id}`}
                            className={`px-3 py-1.5 rounded-full text-[12px] font-medium transition-colors ${
                                period === p.id ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* ═══ 8 executive KPI cards ═══ */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-kpis">
                <Kpi icon={Users} label="Total Customers"
                     value={k.total_customers.value}
                     sub={`+${d.growth.new_customers_this_period} this period`}
                     delta={{ pct: k.total_customers.pct_change }}
                     tone="gold" testId="kpi-total" />
                <Kpi icon={IndianRupee} label="Paying Customers"
                     value={k.paying_customers.value}
                     sub="Active recurring subscriptions"
                     tone="emerald" testId="kpi-paying" />
                <Kpi icon={Sparkles} label="Active Trials"
                     value={k.active_trials.value}
                     sub={`${d.trials.ending_7d} ending within 7 days`}
                     testId="kpi-trials" />
                <Kpi icon={LineChart} label="MRR"
                     value={fmtINRCompact(k.mrr_inr.value)}
                     sub={`ARR ${fmtINRCompact(k.arr_inr.value)}`}
                     tone="gold" testId="kpi-mrr" />
                <Kpi icon={LineChart} label="ARR"
                     value={fmtINR(k.arr_inr.value)}
                     sub="Annualised recurring"
                     testId="kpi-arr" />
                <Kpi icon={TrendingUp} label="Trial → Paid"
                     value={`${k.trial_to_paid_pct.value}%`}
                     sub={k.trial_to_paid_pct.tracking_since === null ? "Tracking begins from launch" : ""}
                     testId="kpi-conv" />
                <Kpi icon={AlertTriangle} label="Customer Churn"
                     value={`${k.customer_churn_pct.value}%`}
                     sub="Cancelled / total"
                     testId="kpi-churn" />
                <Kpi icon={Building2} label="Active Workspaces"
                     value={k.active_workspaces.value}
                     sub="Live customer tenants"
                     testId="kpi-workspaces" />
            </div>

            {/* ═══ Plan distribution + Trial pipeline ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-plans">
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Plan distribution</div>
                    {d.plan_distribution.length === 0 ? (
                        <div className="text-sm text-stone-500">No paying customers yet — tracking begins from your first activation.</div>
                    ) : (
                        <div className="space-y-2">
                            {d.plan_distribution.map((p) => (
                                <div key={p.plan} className="flex items-center gap-3">
                                    <span className="text-[13px] font-medium text-stone-800 w-24 capitalize">{p.plan}</span>
                                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-amber-500" style={{ width: `${Math.min(100, (p.count / Math.max(1, k.total_customers.value)) * 100)}%` }} />
                                    </div>
                                    <span className="text-[12px] tabular-nums text-stone-600 w-10 text-right">{p.count}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-trial-pipeline">
                    <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone-500 mb-3">Trial pipeline · attention</div>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="text-center rounded-xl border border-rose-200 bg-rose-50/40 p-3">
                            <div className="text-2xl font-semibold text-rose-800 tabular-nums">{d.trials.ending_1d}</div>
                            <div className="text-[10px] uppercase tracking-widest text-rose-700 mt-1">Ends in 1d</div>
                        </div>
                        <div className="text-center rounded-xl border border-amber-200 bg-amber-50/60 p-3">
                            <div className="text-2xl font-semibold text-amber-800 tabular-nums">{d.trials.ending_3d}</div>
                            <div className="text-[10px] uppercase tracking-widest text-amber-800 mt-1">Ends in 3d</div>
                        </div>
                        <div className="text-center rounded-xl border border-blue-200 bg-blue-50/40 p-3">
                            <div className="text-2xl font-semibold text-blue-800 tabular-nums">{d.trials.ending_7d}</div>
                            <div className="text-[10px] uppercase tracking-widest text-blue-800 mt-1">Ends in 7d</div>
                        </div>
                    </div>
                    <div className="mt-4 text-[12px] text-stone-500">
                        Total active trials: <span className="font-semibold text-stone-800">{d.trials.active}</span>
                    </div>
                </div>
            </div>

            {/* ═══ Engagement · Support · Payments ═══ */}
            <Section title="Engagement · Support · Payments" subtitle="Metadata only. Financial values never enter analytics.">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-engagement">
                        <div className="flex items-center gap-2 mb-3">
                            <Activity size={13} className="text-stone-500" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Engagement</span>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                            <div><div className="text-lg font-semibold tabular-nums">{d.engagement.dau}</div><div className="text-[10px] text-stone-500 uppercase tracking-widest">DAU</div></div>
                            <div><div className="text-lg font-semibold tabular-nums">{d.engagement.wau}</div><div className="text-[10px] text-stone-500 uppercase tracking-widest">WAU</div></div>
                            <div><div className="text-lg font-semibold tabular-nums">{d.engagement.mau}</div><div className="text-[10px] text-stone-500 uppercase tracking-widest">MAU</div></div>
                        </div>
                        <div className="mt-3 text-[11.5px] text-stone-500">DAU/MAU ratio: <span className="font-semibold text-stone-800">{d.engagement.dau_mau_ratio}%</span></div>
                    </div>
                    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-support">
                        <div className="flex items-center gap-2 mb-3">
                            <HeartHandshake size={13} className="text-stone-500" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Support health</span>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div><div className="text-lg font-semibold tabular-nums">{d.support.open_tickets}</div><div className="text-[10px] text-stone-500 uppercase tracking-widest">Open tickets</div></div>
                            <div><div className={`text-lg font-semibold tabular-nums ${d.support.high_priority > 0 ? "text-rose-700" : ""}`}>{d.support.high_priority}</div><div className="text-[10px] text-stone-500 uppercase tracking-widest">High priority</div></div>
                        </div>
                    </div>
                    <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-payments">
                        <div className="flex items-center gap-2 mb-3">
                            <AlertTriangle size={13} className="text-stone-500" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Payment health</span>
                        </div>
                        <div className="text-lg font-semibold tabular-nums">{d.payments.failed_this_period}</div>
                        <div className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">Failed payments this period</div>
                    </div>
                </div>
            </Section>

            {/* ═══ Geography + Demo Funnel ═══ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-geo">
                    <div className="flex items-center gap-2 mb-3">
                        <Globe size={13} className="text-stone-500" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Geography</span>
                    </div>
                    {d.geography.length === 0 ? (
                        <div className="text-sm text-stone-500">No customers yet.</div>
                    ) : (
                        <div className="space-y-2">
                            {d.geography.map((g) => (
                                <div key={g.country} className="flex items-center gap-3">
                                    <span className="text-[13px] font-medium text-stone-800 w-16">{g.country}</span>
                                    <div className="flex-1 h-2 bg-stone-100 rounded-full overflow-hidden">
                                        <div className="h-full bg-blue-500" style={{ width: `${Math.min(100, (g.customers / Math.max(1, k.total_customers.value)) * 100)}%` }} />
                                    </div>
                                    <span className="text-[12px] tabular-nums text-stone-600 w-8 text-right">{g.customers}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="cc-demo-funnel">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={13} className="text-amber-600" />
                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-stone-500">Public demo funnel</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="text-2xl font-semibold tabular-nums text-stone-900">{d.demo_funnel.demo_opened}</div>
                            <div className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">Demo opened</div>
                        </div>
                        <div>
                            <div className="text-2xl font-semibold tabular-nums text-stone-900">{d.demo_funnel.signup_started}</div>
                            <div className="text-[10px] text-stone-500 uppercase tracking-widest mt-1">Signup started</div>
                        </div>
                    </div>
                    <div className="mt-3 text-[11.5px] text-stone-500">
                        Only anonymous event IDs stored — no PII, no financial values.
                    </div>
                </div>
            </div>

            {/* ═══ Tracking gaps ═══ */}
            {d.tracking_gaps?.length > 0 && (
                <Section title="Tracking gaps · yet to be captured"
                         subtitle="Metrics from §25 acceptance criteria that require additional event capture.">
                    <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-5" data-testid="cc-gaps">
                        {d.tracking_gaps.map((g) => (
                            <div key={g.metric} className="py-1.5 flex items-center gap-2 text-[12.5px]">
                                <Clock size={12} className="text-stone-400" />
                                <span className="font-medium text-stone-800 capitalize">{g.metric.replaceAll("_", " ")}</span>
                                <ChevronRight size={11} className="text-stone-300" />
                                <span className="text-stone-500">{g.reason}</span>
                            </div>
                        ))}
                    </div>
                </Section>
            )}
        </div>
    );
}
